import React, { useState, useEffect, useRef } from 'react';
import {
  FiInfo,
  FiCalendar,
  FiTrendingUp,
  FiRadio,
  FiTrendingDown,
  FiBarChart2,
  FiStar,
  FiActivity,
  FiSmile,
  FiClock,
  FiTarget,
  FiAlertTriangle,
  FiGlobe,
  FiPieChart,
  FiRefreshCw,
  FiExternalLink,
  FiMic, FiZap,
  FiArrowRight,
  FiRepeat,
  FiCompass,
} from 'react-icons/fi';
import { FaNewspaper } from 'react-icons/fa';
import { renderMarketText } from '../utils/marketText';
import BriefVisual from './BriefVisual';
import './MobileBadgeTape.css';

const TYPE_CONFIG = {
  summary: {
    label: 'Summary',
    icon: FiInfo,
    className: 'badge-summary',
  },
  news: {
    label: 'Market News',
    icon: FaNewspaper,
    className: 'badge-news',
  },
  market: {
    label: 'Market Insight',
    icon: FiBarChart2,
    className: 'badge-market',
  },
  breadth: {
    label: 'Market Breadth',
    icon: FiActivity,
    className: 'badge-breadth',
  },
  sentiment: {
    label: 'Sentiment',
    icon: FiSmile,
    className: 'badge-sentiment',
  },
  cycle: {
    label: 'Market Cycle',
    icon: FiClock,
    className: 'badge-cycle',
  },
  sector: {
    label: 'Sector Highlight',
    icon: FiTarget,
    className: 'badge-sector',
  },
  macro: {
    label: 'Macro',
    icon: FiGlobe,
    className: 'badge-macro',
  },
  risk: {
    label: 'Risk Watch',
    icon: FiAlertTriangle,
    className: 'badge-risk',
  },
  event: {
    label: 'Economic Event',
    icon: FiCalendar,
    className: 'badge-event',
  },
  portfolioEarnings: {
    label: 'Upcoming Earnings',
    icon: FiCalendar,
    className: 'badge-portfolio-earnings',
  },
  stockNews: {
    label: 'Stock In The News',
    icon: FiRadio,
    className: 'badge-stock-news',
  },
  signal: {
    label: 'Strong Signal',
    icon: FiTrendingUp,
    className: 'badge-signal',
  },
  sellSignal: {
    label: 'Sell Signal',
    icon: FiTrendingDown,
    className: 'badge-sell',
  },
  list: {
    label: 'List Highlight',
    icon: FiStar,
    className: 'badge-list',
  },
  mover: {
    label: 'Top Gainer',
    icon: FiTrendingUp,
    className: 'badge-mover-up',
  },
  moverDown: {
    label: 'Top Decliner',
    icon: FiTrendingDown,
    className: 'badge-mover-down',
  },
  moverTheme: {
    label: 'Shared Driver',
    icon: FiZap,
    className: 'badge-mover-theme',
  },
  // Leading / lagging smart lists (issue #341) borrow the movers' palette: up is up.
  listUp: {
    label: 'Leading List',
    icon: FiTrendingUp,
    className: 'badge-mover-up',
  },
  listDown: {
    label: 'Lagging List',
    icon: FiTrendingDown,
    className: 'badge-mover-down',
  },
  // Asset-class crowding (issue #372). Blue like the desktop tab, not the movers' green / red:
  // bought-hard is not "good".
  bucketFlow: {
    label: 'Asset classes',
    icon: FiRepeat,
    className: 'badge-buckets',
  },
  callHighlight: {
    label: 'Earnings Call',
    icon: FiMic,
    className: 'badge-call',
  },
  // Guidance by season (issue #387) shares the call palette: it is the calls, summed up.
  guidanceSeason: {
    label: 'Guidance',
    icon: FiCompass,
    className: 'badge-call',
  },
  bond: {
    label: 'Bond Insight',
    icon: FiPieChart,
    className: 'badge-bond',
  },
  default: {
    label: 'Insight',
    icon: FiInfo,
    className: 'badge-default',
  },
};

const MobileBadgeTape = ({ items = [], onRefresh, onLoadMore, hasMore = false, isRefreshing = false, isLoadingMore = false, onOpenList }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const threshold = 70;
  const containerRef = useRef(null);
  const bottomRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!isRefreshing) {
      setPullDistance(0);
      setIsPulling(false);
      setTouchStartY(null);
    }
  }, [isRefreshing]);

  const handleTouchStart = (event) => {
    // Check if container is at or above viewport top (allows pull when scrolled to feed)
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      // Only allow pull-to-refresh if container top is at or above viewport top
      if (rect.top < -5) return; // Small tolerance for touch imprecision
    }
    if (isRefreshing || typeof onRefresh !== 'function') return;
    setTouchStartY(event.touches[0].clientY);
    setIsPulling(true);
  };

  const handleTouchMove = (event) => {
    if (!isPulling || touchStartY === null || isRefreshing || typeof onRefresh !== 'function') {
      return;
    }
    const currentY = event.touches[0].clientY;
    const distance = Math.max(0, currentY - touchStartY);
    // Check container position instead of window.scrollY
    const container = containerRef.current;
    const isAtTop = container ? container.getBoundingClientRect().top >= -5 : false;
    if (distance > 0 && isAtTop) {
      event.preventDefault();
      setPullDistance(distance);
    }
  };

  const handleTouchEnd = () => {
    if (isRefreshing || typeof onRefresh !== 'function') {
      setPullDistance(0);
      setIsPulling(false);
      setTouchStartY(null);
      return;
    }

    if (pullDistance > threshold) {
      onRefresh();
    }

    setPullDistance(0);
    setIsPulling(false);
    setTouchStartY(null);
  };

  const offset = isRefreshing ? 60 : Math.min(pullDistance, 80);
  const showRefresh = pullDistance > 0 || isRefreshing;
  const refreshMessage = isRefreshing
    ? 'Refreshing…'
    : pullDistance > threshold
      ? 'Release to refresh'
      : 'Pull down to refresh';

  const trackClassName = `mobile-feed-track${offset ? ' is-offset' : ''}${isPulling ? ' pulling' : ''}`;
  const trackStyle = offset ? { transform: `translateY(${offset}px)` } : undefined;

  useEffect(() => {
    observerRef.current?.disconnect();

    if (!hasMore || typeof onLoadMore !== 'function') {
      return undefined;
    }

    const element = bottomRef.current;
    if (!element) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore) {
          observer.disconnect();
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '120px',
        threshold: 0.1,
      }
    );
    observer.observe(element);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [hasMore, onLoadMore, items.length, isLoadingMore]);

  if (!items.length) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="mobile-feed md:hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div className={`mobile-feed-refresh ${showRefresh ? 'visible' : ''} ${isRefreshing ? 'refreshing' : ''}`}>
        <FiRefreshCw className={`refresh-icon ${isRefreshing ? 'spin' : ''}`} size={16} />
        <span className="refresh-message">{refreshMessage}</span>
      </div>
      <div className={trackClassName} style={trackStyle}>
        {items.map((item, index) => {
          const {
            type = 'default',
            href,
            title,
            fullTitle,
            subtitle,
            fullSubtitle,
            meta,
            label,
            id,
            listId,
            visual,
          } = item;
          const config = TYPE_CONFIG[type] || TYPE_CONFIG.default;
          const Icon = config.icon;
          const key = `${type}-${id || index}`;
          const itemClasses = `mobile-feed-item${index === items.length - 1 ? ' is-last' : ''}`;
          const isExpanded = expandedId === key;

          const handleToggle = () => {
            setExpandedId((prev) => (prev === key ? null : key));
          };

          const handleLinkClick = (event) => {
            event.stopPropagation();
          };

          return (
            <div key={key} className={itemClasses}>
              <div className="mobile-feed-marker">
                <span className={`mobile-feed-node ${config.className}`} />
              </div>
              <div
                className={`mobile-feed-card ${config.className} ${isExpanded ? 'expanded' : ''}`}
                onClick={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleToggle();
                  }
                }}
              >
                {href && (
                  <a
                    className="mobile-feed-external"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open link in new tab"
                    onClick={handleLinkClick}
                  >
                    <FiExternalLink size={14} />
                  </a>
                )}
                {listId && typeof onOpenList === 'function' && (
                  <button
                    type="button"
                    className="mobile-feed-external"
                    aria-label="Open this list"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenList(listId);
                    }}
                  >
                    <FiArrowRight size={14} />
                  </button>
                )}
                <BadgeContent
                  icon={Icon}
                  label={label || config.label}
                  title={isExpanded && fullTitle ? fullTitle : title}
                  subtitle={isExpanded && fullSubtitle ? fullSubtitle : subtitle}
                  meta={meta}
                  visual={visual}
                  accentClass={config.className}
                  isExpanded={isExpanded}
                />
              </div>
            </div>
          );
        })}
        {(hasMore && typeof onLoadMore === "function") && (
          <div className="mobile-feed-footer" ref={bottomRef}>
            <button
              type="button"
              className={`mobile-feed-more${isLoadingMore ? ' loading' : ''}`}
              onClick={() => {
                if (!isLoadingMore && typeof onLoadMore === 'function') {
                  onLoadMore();
                }
              }}
              disabled={isLoadingMore}
            >
              <span>{isLoadingMore ? 'Loading more…' : 'Load more insights'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const BadgeContent = ({ icon: Icon, label, title, subtitle, meta, visual, accentClass, isExpanded }) => (
  <>
    <div className={`mobile-feed-icon ${accentClass}`}>
      <Icon size={18} />
    </div>
    <div className={`mobile-feed-body ${isExpanded ? 'expanded' : ''}`}>
      <span className="mobile-feed-label">{label}</span>
      {title && <span className="mobile-feed-title">{renderMarketText(title)}</span>}
      {subtitle && <span className="mobile-feed-subtitle">{renderMarketText(subtitle)}</span>}
      {meta && <span className="mobile-feed-meta">{renderMarketText(meta)}</span>}
      {visual && <BriefVisual visual={visual} isExpanded={isExpanded} />}
    </div>
  </>
);

export default MobileBadgeTape;
